import { createCss } from '@rybr/styled-modules'

// CSS variable scope prefix for theming to help reduce naming clashes with third party css injections
export const CSS_VARIABLE_SCOPE_PREFIX = 'rona-ds-'

/**
 * Creates a theme with prefixed CSS variables and a mapping to the raw theme keys.
 */
export const createTheme = <K extends string, P extends string = typeof CSS_VARIABLE_SCOPE_PREFIX>(options: {
  theme: { [k in K]: string }
  prefix?: P
}) => {
  const { theme: rawTheme, prefix = CSS_VARIABLE_SCOPE_PREFIX as P } = options

  /**
   * Used in the theme provider to enforce scoping on the generated css variables
   */
  const prefixedTheme = createCss(
    (Object.keys(rawTheme) as Array<K>).reduce(
      (acc, key) => {
        acc[`${prefix}${key}`] = rawTheme[key]
        return acc
      },
      {} as { [k in `${P}${K}`]: string }
    )
  )

  /**
   * Used in the JSX - maps the raw theme keys to their corresponding CSS variable references.
   * Used by the scripts when generating the SCSS variables.
   * NB: CSS variable prefixing is not supported by styled-modules so we are manually creating the css variable reference
   */
  const theme = Object.fromEntries(
    (Object.keys(rawTheme) as Array<K>).map((key) => [key, prefixedTheme[`${prefix}${key}`]])
  ) as {
    [K2 in K]: (typeof prefixedTheme)[`${P}${K2}`]
  }

  return { prefixedTheme, theme }
}
